import CryptoJS from 'crypto-js';
import Cookies from 'universal-cookie';
import callApi from 'hooks/useApi';
import { ALL_ORGANIZATIONS } from 'utils/postLoginLanding';
import {
  CONTEXT_TYPE_MANAGER,
  CONTEXT_TYPE_MY_INSIGHT,
  applyAccountContext,
  clearPostLoginPending,
  extractLiveAccountContexts,
  getInitialSelectedContext,
  isSecondaryAdminSession,
  loadLoginSnapshot,
  loadPersistedAccountContexts,
  loadPostLoginPending,
  persistEnrichedLiveContexts,
} from 'utils/accountContext/accountContextUtils';

const cookies = new Cookies();

/**
 * Map persisted picker contexts back to the API accountContexts shape so switch
 * payloads keep per-account accessType for multi-admin resolution.
 */
function accountContextsForSwitchPayload(snapshotData, snapshot) {
  const fromSnapshot = snapshotData.accountContexts || snapshot.accountContexts;
  if (Array.isArray(fromSnapshot) && fromSnapshot.length > 0) {
    return fromSnapshot;
  }
  const persisted = loadPersistedAccountContexts();
  if (!Array.isArray(persisted) || persisted.length === 0) {
    return [];
  }
  return persisted.map((context) => ({
    contextType: context.contextType,
    accountId: context.accountId,
    role: context.role,
    accessType: context.accessType,
    accountName: context.accountName,
    isDefault: context.isDefault === true,
    isVerified: context.isVerified !== false,
  }));
}

export function buildSwitchLoginPayload() {
  const snapshot = loadLoginSnapshot() || loadPostLoginPending() || {};
  const snapshotData = snapshot.data || {};
  const accountContexts = accountContextsForSwitchPayload(snapshotData, snapshot);
  return {
    data: {
      _id: snapshotData._id || cookies.get('_Id'),
      email: snapshotData.email || cookies.get('email') || cookies.get('user_email') || localStorage.getItem('email'),
      userId: snapshotData.userId || cookies.get('userId') || cookies.get('userid') || localStorage.getItem('UserId'),
      accountId: snapshotData.accountId || snapshot.accountId || cookies.get('accountId'),
      accessType: snapshotData.accessType || snapshot.accessType || cookies.get('accessType'),
      UserRole: snapshotData.UserRole || [],
      supportAccessSt: snapshotData.supportAccessSt,
      grantedAccountIds: snapshotData.grantedAccountIds,
      ...(accountContexts.length > 0 ? { accountContexts } : {}),
    },
    accountId: snapshot.accountId || snapshotData.accountId || cookies.get('accountId'),
    accessType: snapshot.accessType || snapshotData.accessType,
  };
}

/**
 * Apply a selected account context and land on the right post-login / dashboard route.
 * Soft-reload path (switchMode) matches the previous Switch Account page behavior.
 */
export async function continueWithAccountContext({
  selected,
  loginPayload,
  switchMode = false,
  navigate,
  updateOrganizationContext,
  refreshHierarchyTree,
  allOrganizationsLabel = 'All Organizations',
  onManagerAccessRevoked,
}) {
  if (!selected || !loginPayload || !navigate) {
    return { ok: false, reason: 'missing-input' };
  }

  let contextToApply = selected;
  try {
    const result = await callApi('GET_ACCOUNT_CONTEXTS');
    const { ok, contexts: liveContexts } = extractLiveAccountContexts(result);
    if (ok) {
      const live = persistEnrichedLiveContexts(liveContexts);
      const match = live.find((c) => c.id === selected.id);
      if (!match) {
        if (typeof onManagerAccessRevoked === 'function') {
          onManagerAccessRevoked(live);
        }
        return { ok: false, reason: 'revoked', contexts: live };
      }
      contextToApply = match;
    }
  } catch {
    // Keep the selected context if live refresh fails.
  }

  applyAccountContext(contextToApply, loginPayload, cookies);

  if (!switchMode) {
    clearPostLoginPending();
  } else if (typeof updateOrganizationContext === 'function') {
    updateOrganizationContext('', '0', '', {
      ...ALL_ORGANIZATIONS,
      label: allOrganizationsLabel,
    });
    if (typeof refreshHierarchyTree === 'function') {
      refreshHierarchyTree();
    }
  }

  if (contextToApply.contextType === CONTEXT_TYPE_MY_INSIGHT) {
    const email = loginPayload.data?.email || '';
    cookies.set(
      'isLocNeedToAssg',
      CryptoJS.AES.encrypt('no', email.split('@')[0].toLowerCase()).toString(),
      { secure: Boolean(process.env.secureCoockie), path: '/' }
    );
  }

  return navigateAfterAccountContext({
    selectedContext: contextToApply,
    loginPayload: switchMode ? { tourCompleted: true } : loginPayload,
    switchMode,
    navigate,
    updateOrganizationContext,
    onManagerAccessRevoked,
  });
}

async function navigateAfterAccountContext({
  selectedContext,
  loginPayload,
  switchMode,
  navigate,
  updateOrganizationContext,
  onManagerAccessRevoked,
}) {
  const orgDetailsRequest = { page: 0, size: 1, searchText: '' };
  const dashboardState = switchMode
    ? {
        accountSwitched: true,
        switchedAccountId: cookies.get('accountId'),
        switchedAt: Date.now(),
      }
    : undefined;

  try {
    const response = await callApi('GET_MSP_ORG_DETAILS', orgDetailsRequest, {}, []);

    const orgList = response?.info || [];
    const orgCount = response?.details?.count || 0;
    const startTour = loginPayload?.tourCompleted === false;

    if (orgList.length === 0 || orgCount === 0) {
      if (selectedContext?.contextType === CONTEXT_TYPE_MANAGER) {
        try {
          const liveResult = await callApi('GET_ACCOUNT_CONTEXTS');
          const { ok, contexts: liveContexts } = extractLiveAccountContexts(liveResult);
          if (ok) {
            const live = persistEnrichedLiveContexts(liveContexts);
            if (typeof onManagerAccessRevoked === 'function') {
              onManagerAccessRevoked(live);
            }
            return { ok: false, reason: 'manager-empty-orgs', contexts: live };
          }
        } catch {
          // Keep the current list.
        }
        return { ok: false, reason: 'manager-empty-orgs' };
      }
      if (isSecondaryAdminSession() || String(selectedContext?.role) === '7') {
        localStorage.setItem('userHadOrganizationsAtLogin', 'true');
        if (typeof updateOrganizationContext === 'function') {
          const { applyNewUserOnboardingScope } = await import('utils/postLoginLanding');
          applyNewUserOnboardingScope(updateOrganizationContext);
        }
        navigate('/mspHome/dashboard', { replace: true, state: dashboardState });
        return { ok: true };
      }
      localStorage.setItem('userHadOrganizationsAtLogin', 'false');
      if (typeof updateOrganizationContext === 'function') {
        const { applyNewUserOnboardingScope } = await import('utils/postLoginLanding');
        applyNewUserOnboardingScope(updateOrganizationContext);
      }
      navigate('/newLogin', { replace: true, state: dashboardState });
      return { ok: true };
    }

    localStorage.setItem('userHadOrganizationsAtLogin', 'true');
    if (startTour) {
      navigate('/mspHome/dashboard?startTour=true', { replace: true, state: dashboardState });
    } else {
      navigate('/mspHome/dashboard', { replace: true, state: dashboardState });
    }
    return { ok: true };
  } catch {
    navigate('/mspHome/dashboard', { replace: true, state: dashboardState });
    return { ok: true };
  }
}

export function resolveFallbackContext(liveContexts) {
  return getInitialSelectedContext(liveContexts, {
    switchMode: true,
    activeAccountId: cookies.get('accountId'),
  });
}
